import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox'; // Assuming checkbox exists or will be created
import adminApi from '../../../services/adminApi';

const CustomerFormPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    isVerified: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      setIsEditing(true);
      setIsLoading(true);
      adminApi.getUserById(id)
        .then(response => {
          if (response.success) {
            const { user } = response;
            setFormData({
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              email: user.email || '',
              phone: user.phone || '',
              password: '', // Don't pre-fill password
              isVerified: user.isVerified || false,
            });
          } else {
            setError(response.error || 'Failed to fetch customer data');
          }
        })
        .catch(err => setError(err.toString()))
        .finally(() => setIsLoading(false));
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const dataToSubmit = { ...formData };
    if (isEditing && !dataToSubmit.password) {
      delete dataToSubmit.password;
    }

    try {
      const response = isEditing
        ? await adminApi.updateUser(id, dataToSubmit)
        : await adminApi.createUser(dataToSubmit);

      if (response.success) {
        navigate('/admin/customers');
      } else {
        setError(response.error || 'Failed to save customer');
      }
    } catch (err) {
      setError(err.toString());
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && isEditing) {
    return <div className="p-6">Loading customer data...</div>;
  }

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>{isEditing ? 'Edit Customer' : 'Create Customer'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded">
                Error: {error}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name*</Label>
                <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name*</Label>
                <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email*</Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password {isEditing ? '(leave blank to keep current)' : '*'}</Label>
              <Input id="password" name="password" type="password" value={formData.password} onChange={handleInputChange} required={!isEditing} />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="isVerified" name="isVerified" checked={formData.isVerified} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isVerified: checked }))} />
              <Label htmlFor="isVerified">Is Verified</Label>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/customers')}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Customer'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
};

export default CustomerFormPage;
